import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "../lib/images";
import { FadeUp, MaskedLines, SectionHeading } from "./Reveal";
import { Leaf, FloatingLeaves } from "./Decor";

export function ParallaxStory() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const leafRot = useTransform(scrollYProgress, [0, 1], [-10, 14]);

  return (
    <section ref={ref} data-testid="parallax-story-section" className="relative overflow-hidden py-32 sm:py-44">
      <motion.div style={{ y }} className="absolute -inset-y-[16%] inset-x-0">
        <img
          src={IMAGES.parallax}
          alt="Morning sunlight over the green fields of Unique Organic Farm"
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-nightgreen/65" />
      <motion.div style={{ rotate: leafRot }} className="absolute left-[8%] top-[16%] text-goldlight/40 w-10 h-10" aria-hidden="true">
        <Leaf className="w-full h-full" />
      </motion.div>
      <motion.div style={{ rotate: leafRot }} className="absolute right-[10%] bottom-[14%] text-goldlight/30 w-14 h-14" aria-hidden="true">
        <Leaf className="w-full h-full" />
      </motion.div>

      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <FadeUp>
          <span className="inline-flex items-center gap-3 text-goldlight text-xs font-semibold uppercase tracking-[0.35em]">
            <span className="h-px w-8 bg-gold/70" />
            Our Promise
            <span className="h-px w-8 bg-gold/70" />
          </span>
        </FadeUp>
        <h2 className="mt-6 font-serif text-cream text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] text-balance">
          <MaskedLines inView lines={["Grown With Nature.", "Delivered With Care."]} />
        </h2>
        <FadeUp delay={0.35}>
          <p className="mt-8 text-cream/80 text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto">
            Every product that leaves Unique Organic Farm begins in open soil under the morning sun. We grow
            patiently, harvest by hand and pack with care — so what arrives at your home still carries the
            freshness of the field it came from.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

const ABOUT_POINTS = [
  "Natural cultivation in open farm soil",
  "Farm-fresh produce, harvested by hand",
  "Quality checked at every step",
  "Sustainable practices that protect the land",
  "Healthy, well-rooted nursery plants",
  "Direct farm-to-customer experience",
];

const STATS = [
  { value: "100%", label: "Farm-Direct" },
  { value: "3", label: "Specialist Categories" },
  { value: "18", label: "Curated Products" },
  { value: "1", label: "Family-Run Farm" },
];

export function About() {
  return (
    <section id="about" data-testid="about-section" className="bg-cream py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <div className="relative">
          <FadeUp>
            <div className="relative w-[78%]">
              <div className="arch-frame overflow-hidden shadow-2xl">
                <img
                  src={IMAGES.about[0]}
                  alt="Lush green crop rows at Unique Organic Farm"
                  loading="lazy"
                  className="w-full aspect-[3/4] object-cover hover:scale-105 transition-transform duration-[1400ms]"
                />
              </div>
              <div className="absolute -inset-3 arch-frame border border-gold/40 -z-10 translate-x-4 translate-y-4" aria-hidden="true" />
            </div>
          </FadeUp>
          <FadeUp delay={0.2} className="absolute right-0 bottom-6 sm:bottom-10 w-[46%]">
            <img
              src={IMAGES.about[1]}
              alt="Farmer harvesting fresh produce"
              loading="lazy"
              className="w-full aspect-square object-cover rounded-2xl border-[6px] border-cream shadow-xl"
            />
          </FadeUp>
          <div className="absolute -left-4 top-8 text-sage/60 w-9 h-9 animate-float-leaf" aria-hidden="true">
            <Leaf className="w-full h-full" />
          </div>
        </div>

        <div>
          <SectionHeading
            testId="about-heading"
            eyebrow="About Unique Organic Farm"
            title="Our Farm, Our Passion"
            subtitle="Unique Organic Farm grows natural agricultural products, fruits, spices and nursery plants — nurtured patiently and delivered straight from our fields to your family."
          />
          <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-4">
            {ABOUT_POINTS.map((point, i) => (
              <FadeUp key={point} delay={0.05 * i} className="flex items-start gap-3">
                <span className="mt-0.5 text-gold shrink-0">
                  <Leaf className="w-4 h-4" />
                </span>
                <span className="text-sm text-ink/80 font-light leading-relaxed">{point}</span>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.25}>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-sand pt-8">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-serif text-3xl sm:text-4xl font-semibold text-forest">{s.value}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-muted">{s.label}</div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

export function Philosophy() {
  return (
    <section id="farm-story" data-testid="farm-story-section" className="bg-beige py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
        <div className="order-2 lg:order-1">
          <SectionHeading
            testId="philosophy-heading"
            eyebrow="Farm Story & Philosophy"
            title="Rooted in the Soil, Guided by the Seasons"
          />
          <FadeUp delay={0.15}>
            <p className="mt-6 text-base font-light text-ink/80 leading-relaxed">
              Our farm sits in the Indian countryside, where farming is less an industry and more a way of life.
              We work with the rhythm of the seasons — preparing the soil, sowing, tending and harvesting — the
              way it has been done for generations.
            </p>
          </FadeUp>
          <FadeUp delay={0.22}>
            <p className="mt-4 text-base font-light text-ink/80 leading-relaxed">
              From turmeric beds to orchard rows and nursery shade-houses, everything we grow is raised close to
              the earth and close to home. When you buy from us, you buy directly from the people who grow it.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <blockquote className="mt-8 border-l-2 border-gold pl-6 font-serif italic text-xl sm:text-2xl text-forest leading-snug">
              “We don't hurry the harvest. The farm knows when it's ready — our job is simply to care for it.”
            </blockquote>
          </FadeUp>
        </div>

        <div className="order-1 lg:order-2 relative">
          <FadeUp>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="absolute inset-0 spotlight z-10" aria-hidden="true" />
              <img
                src={IMAGES.philosophy[0]}
                alt="Farmer's hands holding rich farm soil"
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.2} className="absolute -bottom-8 -left-4 sm:-left-10 w-40 sm:w-52 hidden sm:block">
            <img
              src={IMAGES.philosophy[1]}
              alt="Sunlit green leaves on the farm"
              loading="lazy"
              className="w-full aspect-square object-cover rounded-xl border-[5px] border-beige shadow-xl"
            />
          </FadeUp>
        </div>
      </div>
      <FloatingLeaves className="!fixed hidden" />
    </section>
  );
}
